import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FileText, Loader2, PenLine } from 'lucide-react';
import { Button } from '../components/ui/button';
import { fetchApplicationKitSummary, safeError } from '../data/candidateApi';
import { fetchSingleJobResult, generateTailoredCv, requestCoverLetter } from '../data/mockJobResults';

function allowanceCopy(summary) {
  const used = Number(summary?.successful_kit_count ?? summary?.successful_kits_used ?? summary?.used ?? 0);
  const allowance = Number(summary?.free_successful_kits ?? summary?.free_kit_allowance ?? 4) || 4;
  return `Free application kits: ${Math.min(used, allowance)} of ${allowance} used before generation.`;
}

export default function ApplicationPackagePage() {
  const { jobResultId } = useParams();
  const [job, setJob] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [jobPayload, kitPayload] = await Promise.all([fetchSingleJobResult(jobResultId), fetchApplicationKitSummary()]);
        if (!alive) return;
        setJob(jobPayload);
        setSummary(kitPayload);
      } catch (err) { if (alive) setError(safeError(err)); }
      finally { if (alive) setLoading(false); }
    }
    load();
    return () => { alive = false; };
  }, [jobResultId]);

  const unavailable = Boolean(job?.unavailable || job?.withdrawn_at || job?.is_unavailable);
  const eligible = Boolean(job?.application_eligible ?? job?.fit !== 'Weak') && !unavailable;

  async function generate(kind) {
    if (!eligible) return;
    setWorking(kind);
    setError('');
    try {
      if (kind === 'cv') await generateTailoredCv(jobResultId, { force_regenerate: false });
      if (kind === 'letter') await requestCoverLetter({ job_result_id: jobResultId, force_regenerate: false });
    } catch (err) { setError(safeError(err, 'We could not start that document right now.')); }
    finally { setWorking(''); }
  }

  return (
    <main className="min-h-screen bg-[#f6efe3] px-5 py-10 text-[#22362f]" data-testid="application-package-page">
      <div className="mx-auto max-w-5xl">
        <Link to="/my-jobs" className="text-sm underline" data-testid="application-package-back-link">Back to My Jobs</Link>
        <h1 className="mt-4 font-serif-display text-4xl font-medium sm:text-5xl" data-testid="application-package-title">Application package</h1>
        <div className="jmp-panel mt-6 p-5" data-testid="application-package-kit-allowance">{allowanceCopy(summary)}</div>
        {loading && <div className="mt-6 border border-[#d8c8ab] bg-[#fff9ee] p-6" data-testid="application-package-loading">Loading package…</div>}
        {error && <div className="mt-6 border border-red-200 bg-red-50 p-6 text-sm text-red-700" data-testid="application-package-error">{error}</div>}
        {!loading && job && (
          <>
            <section className="jmp-panel mt-6 p-5" data-testid="application-package-job-summary">
              <p className="text-sm text-[#5f6c64]">{job.company || 'Company not listed'} · {job.location || 'Location not listed'}</p>
              <h2 className="mt-2 font-serif-display text-3xl font-medium">{job.job_title || job.title || 'Untitled role'}</h2>
              {unavailable && <p className="mt-4 text-sm text-red-700" data-testid="application-package-unavailable">This role is unavailable. Existing documents remain visible; new generation is disabled.</p>}
              {job.fit === 'Weak' && <p className="mt-4 text-sm text-[#5f6c64]" data-testid="application-package-weak-note">This result is Weak, so preparation is not available for this role.</p>}
            </section>
            <section className="mt-6 grid gap-4 md:grid-cols-2" aria-label="Application package artifacts" data-testid="application-package-artifacts">
              <article className="jmp-card-interactive border-t-4 border-t-[#3B82F6] p-5" data-testid="application-package-tailored-cv">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#ECE7DC]"><FileText className="h-6 w-6 text-[#10231D]" aria-hidden="true" /></span>
                <h3 className="mt-4 font-serif-display text-2xl font-medium">Tailored CV</h3>
                <p className="mt-2 text-sm text-[#5f6c64]">Review, edit, and download the CV for this role.</p>
                <Button type="button" className="btn-primary mt-5" disabled={!eligible || working === 'cv'} onClick={() => generate('cv')} data-testid="generate-tailored-cv-button">{working === 'cv' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Generate Tailored CV</Button>
              </article>
              <article className="jmp-card-interactive border-t-4 border-t-[#10231D] p-5" data-testid="application-package-cover-letter">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#ECE7DC]"><PenLine className="h-6 w-6 text-[#10231D]" aria-hidden="true" /></span>
                <h3 className="mt-4 font-serif-display text-2xl font-medium">Customizable Cover Letter</h3>
                <p className="mt-2 text-sm text-[#5f6c64]">Generate a concise letter you can edit before using.</p>
                <Button type="button" className="btn-primary mt-5" disabled={!eligible || working === 'letter'} onClick={() => generate('letter')} data-testid="generate-cover-letter-button">{working === 'letter' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Generate Cover Letter</Button>
              </article>
            </section>
            <Link to={`/job-results/${encodeURIComponent(jobResultId)}/editor`} className="btn-secondary mt-6 inline-flex items-center px-5 text-sm font-medium" data-testid="application-package-editor-link">Open editor</Link>
          </>
        )}
      </div>
    </main>
  );
}